export interface DebouncedFunction<T extends (...args: never[]) => void> {
  (...args: Parameters<T>): Promise<ReturnType<T>>;
  cancel(): void;
  flush(): void;
}

/**
 * Creates a debounced version of a function that delays its execution
 * until after `wait` milliseconds have elapsed since the last time it was invoked.
 *
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns A debounced version of the provided function
 */
export function debounce<T extends (...args: never[]) => unknown>(
  func: T,
  wait: number
): DebouncedFunction<T> & { (): Promise<ReturnType<T>> } {
  let timeoutId: NodeJS.Timeout | undefined;
  let pendingArgs: Parameters<T> | undefined;
  let pendingResolve: ((value: ReturnType<T>) => void) | undefined;
  let pendingReject: ((reason: unknown) => void) | undefined;

  const debounced = function (...args: Parameters<T>): Promise<ReturnType<T>> {
    return new Promise((resolve, reject) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      pendingArgs = args;
      pendingResolve = resolve as (value: ReturnType<T>) => void;
      pendingReject = reject;

      timeoutId = setTimeout(() => {
        try {
          const result = func(...args) as ReturnType<T>;
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          pendingArgs = undefined;
          pendingResolve = undefined;
          pendingReject = undefined;
          timeoutId = undefined;
        }
      }, wait);
    });
  } as DebouncedFunction<T> & { (): Promise<ReturnType<T>> };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
    pendingArgs = undefined;
    pendingResolve = undefined;
    pendingReject = undefined;
  };

  debounced.flush = () => {
    if (timeoutId && pendingArgs && pendingResolve && pendingReject) {
      clearTimeout(timeoutId);
      timeoutId = undefined;

      try {
        const result = func(...pendingArgs) as ReturnType<T>;
        pendingResolve(result);
      } catch (error) {
        pendingReject(error);
      } finally {
        pendingArgs = undefined;
        pendingResolve = undefined;
        pendingReject = undefined;
      }
    }
  };

  return debounced;
}

export function memoize<T extends (...args: never[]) => unknown>(fn: T): T {
  const cache = new Map<string, ReturnType<T>>();
  return function (...args: Parameters<T>): ReturnType<T> {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key) as ReturnType<T>;
    }
    const result = fn(...args) as ReturnType<T>;
    cache.set(key, result);
    return result;
  } as T;
}
